import { Injectable } from '@angular/core';
import { OpenAIBaseService } from './open-ai-base.service';
import { SupabaseService } from './supabase.service';
import { CalibrationService } from './calibration.service';
import { Job } from '../types/job.types';
import { environment } from '../../environments/environment';
import { 
  SYSTEM_PROMPT, 
  ONBOARDING_PROMPT, 
  JOB_SEARCH_PROMPT 
} from './prompts/chat-prompts';

export interface ChatAction {
  type: 'search_jobs' | 'add_signal' | 'remove_signal' | 'update_priority' | 
        'show_job' | 'confirm_signal' | 'complete_onboarding';
  payload: any;
}

export interface ChatResponse {
  message: string;
  actions: ChatAction[];
  suggestedQuickReplies?: string[];
}

export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
  createdAt?: Date;
}

export interface UserIntent {
  type: 'search' | 'preference' | 'feedback' | 'question' | 'onboarding' | 'greeting';
  confidence: number;
  entities: { [key: string]: any };
  originalMessage: string;
}

export interface SuggestedSignal {
  category: string;
  text: string;
  tier: string;
  confirmationQuestion: string;
  needsMoreInfo?: boolean;
  clarifyingQuestion?: string; // Question to ask for specific details
}

@Injectable({
  providedIn: 'root'
})
export class AIChatService extends OpenAIBaseService {
  
  // System prompt is now imported from prompts/chat-prompts.ts

  constructor(
    private supabaseService: SupabaseService,
    private calibrationService: CalibrationService
  ) {
    super();
  }

  /**
   * Process a user message and generate response with actions
   */
  async processMessage(
    userId: string,
    message: string,
    context: { mode: 'onboarding' | 'job_search'; currentJobs?: Job[]; extractedData?: any }
  ): Promise<ChatResponse> {
    try {
      // Save user message to history
      await this.saveMessage(userId, 'user', message, context.mode);

      // Extract intent from the message first (for greetings, we may not need history)
      const intent = await this.extractIntent(message);

      // For greetings, we don't need history at all
      const historyLimit = intent.type === 'greeting' ? 0 : (context.mode === 'onboarding' ? 20 : 10);
      const history = historyLimit > 0 ? await this.getChatHistory(userId, context.mode, historyLimit) : [];

      // Generate AI response
      const response = await this.generateResponse(userId, message, intent, context, history);

      // Save assistant response to history
      await this.saveMessage(userId, 'assistant', response.message, context.mode, {
        actions: response.actions,
        intent: intent
      });

      return response;
    } catch (error) {
      return {
        message: "Sorry, I'm having a bit of trouble right now. Could you try that again?",
        actions: [],
        suggestedQuickReplies: ['Try again', 'Start over']
      };
    }
  }

  /**
   * Extract structured intent from user message
   */
  async extractIntent(message: string): Promise<UserIntent> {
    const prompt = `
Analyze this user message and extract the intent:
"${message}"

Classify the intent type and extract relevant entities.

Intent types (choose the MOST SPECIFIC one):
- "greeting": Simple greetings like "hi", "hello", "hey", "what's up", "how are you"
- "question": User is ASKING something (contains "?", starts with "how", "what", "why", "when", "where", "can you explain", "show me my preferences")
- "search": Looking for jobs or requesting to see matches (e.g., "show me jobs", "any remote roles")
- "preference": Expressing job preferences or filters (e.g., "I want remote", "prefer startups")
- "feedback": Giving feedback on jobs or matches
- "onboarding": Answering questions during onboarding (providing their preferences when asked)

IMPORTANT: 
- "How does this work?" = question (not preference)
- "What are signals?" = question (not onboarding)
- "Show me my preferences" = question (not search)
- "What are my current preferences?" = question (not search)
- "I prefer remote" = preference
- "Remote is fine" (when asked about location) = onboarding

Output JSON only:
{
  "type": "greeting|search|preference|feedback|question|onboarding",
  "confidence": 0.0-1.0,
  "entities": {
    "location": "extracted location if mentioned",
    "role": "job title/role if mentioned", 
    "company_type": "startup/enterprise/etc if mentioned",
    "salary": "salary info if mentioned",
    "skills": ["skill1", "skill2"],
    "work_type": "remote/hybrid/onsite if mentioned"
  }
}`;

    try {
      // Quick check for simple greetings before making API call
      const lowerMessage = message.toLowerCase().trim();
      const simpleGreetings = ['hi', 'hello', 'hey', 'hiya', 'sup', 'yo', "what's up", 'whats up', 'howdy'];
      
      if (simpleGreetings.includes(lowerMessage) || 
          (lowerMessage.length < 15 && /^(hi|hello|hey|hiya)\s*[!.?]*$/i.test(lowerMessage))) {
        return {
          type: 'greeting',
          confidence: 1.0,
          entities: {},
          originalMessage: message
        };
      }
      
      // Quick check for preference-related queries
      if (/update|change|modify|edit/i.test(lowerMessage) && /preference|signal|filter/i.test(lowerMessage)) {
        return {
          type: 'question',
          confidence: 0.9,
          entities: {},
          originalMessage: message
        };
      }
      
      // Quick check for "show me my preferences" or similar
      if (/show.*my.*preference|what.*my.*preference|my.*current.*preference/i.test(lowerMessage)) {
        return {
          type: 'question',
          confidence: 0.95,
          entities: {},
          originalMessage: message
        };
      }

      // Quick check for simple onboarding answers - skip API call for common responses
      const simpleOnboardingAnswers = [
        // Location
        'fully remote', 'remote', 'on-site', 'onsite', 'hybrid', 'flexible', 'any', 'either',
        // Salary
        'flexible', 'open', 'no preference', 'no minimum', 'any', "doesn't matter", 'negotiable',
        // Company type
        'startups', 'startup', 'mid-size', 'midsize', 'enterprise', 'large', 'small',
        'no preference', 'any', 'either'
      ];
      if (simpleOnboardingAnswers.includes(lowerMessage) || 
          /^\$?[\d,]+k\+?$|^€?[\d,]+k\+?$/i.test(lowerMessage) ||  // Min salary like "$100k+", "€80k+"
          /^\$?[\d,]+k?(-\$?[\d,]+k?)?$/.test(lowerMessage) ||  // Salary ranges like "$100k-150k"
          lowerMessage.length < 25) {  // Short answers are likely onboarding responses
        return {
          type: 'onboarding',
          confidence: 0.9,
          entities: {},
          originalMessage: message
        };
      }

      const response = await this.makeOpenAIChatRequest(
        prompt,
        'You are an intent extraction system. Return only valid JSON, no other text.',
        'gpt-4o-mini',
        0.1
      );

      return {
        ...this.parseJsonResponse(response),
        originalMessage: message
      } as UserIntent;
    } catch (error) {
      return {
        type: 'question',
        confidence: 0.5,
        entities: {},
        originalMessage: message
      };
    }
  }

  /**
   * Generate AI response with appropriate actions
   */
  private async generateResponse(
    userId: string,
    message: string,
    intent: UserIntent,
    context: { mode: 'onboarding' | 'job_search'; currentJobs?: Job[]; extractedData?: any },
    history: ChatMessage[]
  ): Promise<ChatResponse> {
    
    // Handle greetings separately - respond naturally without full context
    if (intent.type === 'greeting' && context.mode === 'job_search') {
      const userName = await this.getUserName(userId, context.extractedData);
      const greeting = userName ? `Hey ${userName}!` : 'Hey there!';
      
      return {
        message: `${greeting} How can I help you today?`,
        actions: [],
        suggestedQuickReplies: ['Show me jobs', 'Update my preferences', 'Any remote roles?']
      };
    }
    
    // Handle questions - answer them helpfully
    if (intent.type === 'question') {
      const userName = await this.getUserName(userId, context.extractedData);
      
      // Check if they're asking for their preferences
      const lowerMessage = message.toLowerCase();
      if (lowerMessage.includes('my preferences') || 
          lowerMessage.includes('my current preferences') ||
          lowerMessage.includes('show me my preferences') ||
          lowerMessage.includes('what are my preferences') ||
          lowerMessage.includes('what am i looking for') ||
          lowerMessage.includes('my criteria') ||
          lowerMessage.includes('my settings') ||
          lowerMessage.includes('current preferences') ||
          lowerMessage.includes('show my preferences')) {
        return await this.showUserPreferences(userId, userName);
      }
      
      return await this.answerUserQuestion(message, userName, context);
    }
    
    const contextInfo = this.buildContextInfo(context, history);
    
    // Get user's name for personalization
    const userName = await this.getUserName(userId, context.extractedData);
    
    // For non-greeting messages, only include recent relevant history (last 5 messages)
    const recentHistory = history.slice(-5);
    const conversationHistory = recentHistory
      .map(msg => `${msg.role.toUpperCase()}: ${msg.content}`)
      .join('\n');

    const prompt = context.mode === 'onboarding' 
      ? ONBOARDING_PROMPT(contextInfo, userName, conversationHistory, message)
      : JOB_SEARCH_PROMPT(contextInfo, conversationHistory, message);
    try {
      const response = await this.makeOpenAIChatRequest(
        prompt,
        SYSTEM_PROMPT,
        'gpt-4o-mini',
        0.3
      );

      return this.parseResponseWithActions(response);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Answer user questions about the platform, process, or features
   */
  private async answerUserQuestion(
    question: string,
    userName: string | null,
    context: { mode: 'onboarding' | 'job_search'; currentJobs?: Job[]; extractedData?: any }
  ): Promise<ChatResponse> {
    const lowerQuestion = question.toLowerCase();
    
    // Special case: "Update my preferences" - ask what they want to update
    if (/update|change|modify|edit/i.test(lowerQuestion) && /preference|signal|filter/i.test(lowerQuestion)) {
      return {
        message: "Sure! What would you like to update? You can tell me about location, job titles, company types, salary, or anything else you're looking for.",
        actions: [],
        suggestedQuickReplies: ['Remote only', 'Different job titles', 'Company types', 'Tell me my options']
      };
    }
    
    const prompt = `
User asked: "${question}"

Context: They are in ${context.mode === 'onboarding' ? 'onboarding mode (setting up their preferences)' : 'job search mode (looking for jobs)'}.

Provide a helpful, concise answer (2-3 sentences max). Be friendly and casual.

Common questions and answers:
- "How does this work?" → Explain that you help match them with jobs based on their preferences/signals
- "What are preferences/signals?" → Explain these are their job criteria (location, role, company type, etc.)
- "What happens next?" → Explain the next step in their journey
- "Can you explain X?" → Give a brief, helpful explanation

Keep it under 40 words. Be conversational and helpful.`;

    try {
      const response = await this.makeOpenAIChatRequest(
        prompt,
        'You are Dave, a friendly job matching assistant. Be helpful and concise.',
        'gpt-4o-mini',
        0.3
      );

      return {
        message: response.trim(),
        actions: [],
        suggestedQuickReplies: ['Got it', 'Show me jobs', 'Tell me more']
      };
    } catch (error) {
      return {
        message: "Great question! I help you find jobs that match what you're looking for. Just let me know your preferences and I'll find the best matches. Anything else you'd like to know?",
        actions: [],
        suggestedQuickReplies: ['Show me jobs', 'How does matching work?', 'Continue']
      };
    }
  }

  /**
   * Show user their current preferences
   */
  private async showUserPreferences(userId: string, userName: string | null): Promise<ChatResponse> {
    try {
      // Fetch calibration signals
      const signals = await this.calibrationService.getSignals(userId);
      
      // Fetch user profile for salary info
      const { data: profile } = await this.supabaseService
        .getClient()
        .from('users')
        .select('preferences')
        .eq('id', userId)
        .single();

      // Format the preferences
      const categories = ['location', 'work_type', 'compensation', 'role', 'company_type', 'skills'];
      const preferenceLines: string[] = [];

      for (const categoryKey of categories) {
        const category = signals.find((c: any) => c.category === categoryKey);
        
        if (category) {
          // Combine all tiers (excellentFit, goodFit, borderline, notAFit)
          const allItems = [
            ...(category.excellentFit || []),
            ...(category.goodFit || []),
            ...(category.borderline || []),
            ...(category.notAFit || [])
          ];
          
          if (allItems.length > 0) {
            // Create user-friendly category labels
            const categoryLabels: { [key: string]: string } = {
              'location': 'LOCATION',
              'work_type': 'WORK ARRANGEMENT',
              'compensation': 'COMPENSATION',
              'role': 'ROLE',
              'company_type': 'COMPANY TYPE',
              'skills': 'SKILLS'
            };
            const categoryLabel = categoryLabels[categoryKey] || categoryKey.replace('_', ' ').toUpperCase();
            const signalTexts = allItems
              .map((item: any) => `• ${item.text}`)
              .join('\n');
            
            if (signalTexts) {
              preferenceLines.push(`**${categoryLabel}**\n${signalTexts}`);
            }
          }
        }
      }

      // Add salary from profile if available
      if (profile?.preferences?.salaryMin != null || profile?.preferences?.salaryMax != null) {
        const min = profile.preferences.salaryMin;
        const max = profile.preferences.salaryMax;
        const salaryStr = min != null && max != null
          ? `$${min.toLocaleString()} - $${max.toLocaleString()}`
          : min != null
            ? `Minimum: $${min.toLocaleString()}`
            : `Maximum: $${max.toLocaleString()}`;
        
        preferenceLines.push(`**SALARY FROM PROFILE**\n• ${salaryStr}`);
      }

      const greeting = userName ? `Hey ${userName}! ` : 'Hey! ';
      
      if (preferenceLines.length === 0) {
        return {
          message: `${greeting}You don't have any preferences set yet. Let me know what you're looking for and I'll help match you with jobs!`,
          actions: [],
          suggestedQuickReplies: ['Show me remote jobs', 'I want startups', 'Set my preferences']
        };
      }

      const preferencesText = preferenceLines.join('\n\n');
      
      return {
        message: `${greeting}Here's what you're currently looking for:\n\n${preferencesText}\n\nWant to update any of these?`,
        actions: [],
        suggestedQuickReplies: ['Update preferences', 'Show me jobs', 'Looks good']
      };

    } catch (error) {
      return {
        message: "I'm having trouble fetching your preferences right now. Could you try again?",
        actions: [],
        suggestedQuickReplies: ['Try again', 'Show me jobs']
      };
    }
  }

  /**
   * Get user's first name for personalization
   */
  private async getUserName(userId: string, extractedData?: any): Promise<string | null> {
    try {
      const { data: userData } = await this.supabaseService
        .getClient()
        .from('users')
        .select('first_name, display_name, email')
        .eq('id', userId)
        .single();
      
      if (userData?.first_name) {
        return userData.first_name;
      } else if (userData?.display_name) {
        return userData.display_name.split(' ')[0];
      } else if (userData?.email) {
        const emailName = userData.email.split('@')[0].split('.')[0];
        return emailName.charAt(0).toUpperCase() + emailName.slice(1);
      }
    } catch {
    }

    // Fallback to resume data
    if (extractedData?.aiAnalyzedDetails?.name) {
      return extractedData.aiAnalyzedDetails.name.split(' ')[0];
    }

    return null;
  }

  /**
   * Get chat history for a user
   */
  async getChatHistory(
    userId: string, 
    sessionType?: string, 
    limit: number = 50
  ): Promise<ChatMessage[]> {
    try {
      let query = this.supabaseService
        .getClient()
        .from('chat_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (sessionType) {
        query = query.eq('session_type', sessionType);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return (data || [])
        .reverse()
        .map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          metadata: msg.metadata,
          createdAt: new Date(msg.created_at)
        }));
    } catch {
      return [];
    }
  }

  /**
   * Save message to chat history
   */
  async saveMessage(
    userId: string, 
    role: string, 
    content: string, 
    sessionType: string = 'job_search',
    metadata?: any
  ): Promise<void> {
    try {
      const { error } = await this.supabaseService
        .getClient()
        .from('chat_history')
        .insert({
          user_id: userId,
          role,
          content,
          session_type: sessionType,
          metadata: metadata || {}
        });

      if (error) {
        throw error;
      }
    } catch {
    }
  }

  /**
   * Generate the initial onboarding greeting
   */
  async getNextOnboardingQuestion(userId: string, extractedData: any): Promise<string> {
    const userName = await this.getUserName(userId, extractedData);
    const details = extractedData?.aiAnalyzedDetails;
    const role = details?.targetRoles?.[0] || details?.seniority || null;

    const prompt = `
Write the first 2 messages from Dave (a job matching assistant) to a new user.

User info:
- Name: ${userName || 'Unknown'}
${role ? `- Role from resume: ${role}` : ''}

Message 1: Greeting
- Say hi with their name
- Mention you're Dave, their AI assistant
- ${role ? `Mention you're excited to help them find their next "${role}" role` : 'Brief acknowledgment of their background'}
- Warm and casual, like texting a friend
- One emoji
- Under 25 words

Message 2: First question  
- ${role ? `Ask if they're looking for "${role}" roles in a specific location, or open to remote work` : 'Ask about location or remote work preference'}
- Make it conversational, not robotic
- Under 25 words
- No emoji

Return as JSON only:
{"message1": "...", "message2": "..."}`;

    try {
      const response = await this.makeOpenAIChatRequest(
        prompt,
        'You are a friendly, casual assistant. Return only valid JSON.',
        'gpt-4o-mini',
        0.7
      );

      const parsed = this.parseJsonResponse(response);
      return `${parsed.message1}\n\n${parsed.message2}`;
    } catch (error) {
      const greeting = userName ? `Hey ${userName}!` : 'Hey there!';
      const background = role ? ` Great to see a ${role} here!` : ' Great to have you here!';
      return `${greeting} I'm Dave, your AI assistant 😊${background}\n\nQuick question: are you looking for remote work, or do you have a location preference?`;
    }
  }

  /**
   * Check if onboarding is complete
   */
  async isOnboardingComplete(userId: string): Promise<boolean> {
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('users')
        .select('onboarding_completed')
        .eq('id', userId)
        .single();

      if (error) {
        return false;
      }

      return data?.onboarding_completed || false;
    } catch {
      return false;
    }
  }

  /**
   * Mark onboarding as complete
   */
  async markOnboardingComplete(userId: string): Promise<void> {
    try {
      const { error } = await this.supabaseService
        .getClient()
        .from('users')
        .update({
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Analyze job feedback and suggest calibration signals
   */
  async analyzeFeedback(job: Job, reason: string): Promise<SuggestedSignal | null> {
    const prompt = `
User rejected this job:
- Title: ${job.title}
- Company: ${job.company}
- Location: ${job.display_location || job.location}
- Level: ${job.level}
- Work Type: ${job.work_type || job.employment_type}
${job.salary_min || job.salary_max ? `- Salary: ${job.salary_min ? '$' + job.salary_min.toLocaleString() : ''}${job.salary_min && job.salary_max ? ' - ' : ''}${job.salary_max ? '$' + job.salary_max.toLocaleString() : ''}` : ''}

Reason given: "${reason}"

Should we add a calibration signal based on this feedback?
- CRITICAL: If the reason is "Not interested in this job", ALWAYS return {"shouldAddSignal": false} - this is job-specific feedback, not a preference
- If the feedback is specific to just this job, return {"shouldAddSignal": false}
- If it suggests a broader preference, suggest a signal
- IMPORTANT: If the feedback is vague or needs clarification (e.g., "salary too low", "location not ideal", "company too big"):
  * Set "shouldAddSignal": true
  * Set "needsMoreInfo": true (we'll ask for specifics after they confirm)
  * Provide a general confirmation question that acknowledges the issue
  * Include a "clarifyingQuestion" to ask for the specific details

CRITICAL - Confirmation Question Format:
- The confirmationQuestion should be specific to what the user is filtering
- Use "Should I filter out all [X] positions going forward?" where [X] is the SPECIFIC THING they want to filter
- IMPORTANT: When reason is "Not interested in this company", use the ACTUAL COMPANY NAME from the job
  * Example: If company is "Google" → "Should I filter out all **Google** positions going forward?"
  * Example: If company is "Microsoft" → "Should I filter out all **Microsoft** positions going forward?"
  * NEVER say "company type" or "this company" - use the actual company name
- Examples for other reasons:
  * User said "not interested in HR companies" → "Should I filter out all **HR company** positions going forward?"
  * User said "salary too low" → "Should I filter out all **positions with low salary** going forward?"
  * User said "too junior" → "Should I filter out all **junior** positions going forward?"
  * User said "don't want remote" → "Should I filter out all **remote** positions going forward?"
  * User said "wrong role type" → "Should I filter out all **[role type]** positions going forward?"
- NEVER say "company type" in the confirmation question - always be specific

Output JSON only:
{
  "shouldAddSignal": true/false,
  "needsMoreInfo": true/false,
  "signal": {
    "category": "role|location|work_type|compensation|culture|company_type|skills",
    "text": "Clear description of what to avoid/prefer (can be empty if needsMoreInfo is true)",
    "tier": "not_a_fit",
    "confirmationQuestion": "Should I filter out all [specific thing] positions going forward?"
  },
  "clarifyingQuestion": "Follow-up question to get specific details (e.g., 'What's the minimum salary you're looking for?')"
}

Examples of VAGUE feedback needing clarification:
- "Salary too low" → {"shouldAddSignal": true, "needsMoreInfo": true, "signal": {"category": "compensation", "text": "", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all positions with low salary going forward?"}, "clarifyingQuestion": "What's the minimum salary you're looking for?"}
- "Location not ideal" → {"shouldAddSignal": true, "needsMoreInfo": true, "signal": {"category": "location", "text": "", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out positions in this location going forward?"}, "clarifyingQuestion": "What locations do you prefer?"}
- "Company too big" → {"shouldAddSignal": true, "needsMoreInfo": true, "signal": {"category": "company_type", "text": "", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all large company positions going forward?"}, "clarifyingQuestion": "What company size do you prefer? (e.g., startup, mid-size)"}

Examples of JOB-SPECIFIC feedback (NO signal needed):
- "Not interested in this job" → {"shouldAddSignal": false} (user is rejecting this specific job posting only)
- "Job no longer available" → {"shouldAddSignal": false} (not a preference, just outdated)

Examples of BROADER PREFERENCE feedback (signal needed):
- "Don't want remote work" → {"shouldAddSignal": true, "needsMoreInfo": false, "signal": {"category": "location", "text": "avoid remote positions", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all remote positions going forward?"}}
- "Don't want onsite work" → {"shouldAddSignal": true, "needsMoreInfo": false, "signal": {"category": "work_type", "text": "avoid onsite positions", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all onsite positions going forward?"}}
- "No hybrid roles" → {"shouldAddSignal": true, "needsMoreInfo": false, "signal": {"category": "work_type", "text": "avoid hybrid positions", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all hybrid positions going forward?"}}
- "No junior roles" → {"shouldAddSignal": true, "needsMoreInfo": false, "signal": {"category": "role", "text": "avoid junior positions", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all junior positions going forward?"}}
- "Not interested in this company" (when job.company = "Google") → {"shouldAddSignal": true, "needsMoreInfo": false, "signal": {"category": "company_type", "text": "avoid Google", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all Google positions going forward?"}}
- "Not interested in this company" (when job.company = "Acme Corp") → {"shouldAddSignal": true, "needsMoreInfo": false, "signal": {"category": "company_type", "text": "avoid Acme Corp", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all Acme Corp positions going forward?"}}
- "Not interested in HR companies" → {"shouldAddSignal": true, "needsMoreInfo": false, "signal": {"category": "company_type", "text": "avoid hr human resources recruitment companies", "tier": "not_a_fit", "confirmationQuestion": "Should I filter out all HR company positions going forward?"}}`;

    try {
      const response = await this.makeOpenAIChatRequest(
        prompt,
        'You are a preference analysis system. Return only valid JSON.',
        'gpt-4o-mini',
        0.2
      );

      const result = this.parseJsonResponse(response);
      
      // If we should add a signal, return it with the needsMoreInfo flag and clarifying question
      if (result.shouldAddSignal && result.signal) {
        return {
          ...result.signal,
          needsMoreInfo: result.needsMoreInfo || false,
          clarifyingQuestion: result.clarifyingQuestion || undefined
        };
      }
      
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Build context information for AI responses
   */
  private buildContextInfo(
    context: { mode: 'onboarding' | 'job_search'; currentJobs?: Job[]; extractedData?: any },
    history: ChatMessage[]
  ): string {
    const parts: string[] = [];

    if (context.mode === 'onboarding') {
      parts.push('=== ONBOARDING MODE ===');
      
      if (context.extractedData?.aiAnalyzedDetails) {
        const details = context.extractedData.aiAnalyzedDetails;
        parts.push('\nRESUME DATA (already known, do not ask about):');
        if (details.name) parts.push(`- Name: ${details.name}`);
        if (details.targetRoles?.length) parts.push(`- Target Roles: ${details.targetRoles.join(', ')}`);
        if (details.skills?.length) parts.push(`- Skills: ${details.skills.slice(0, 8).join(', ')}`);
        if (details.seniority) parts.push(`- Seniority: ${details.seniority}`);
      }
      
      const collected = this.analyzeCollectedPreferences(history);
      parts.push('\nPREFERENCES COLLECTED FROM CHAT:');
      parts.push(collected || '- None yet');
      
    } else {
      parts.push('=== JOB SEARCH MODE ===');
      
      // Include resume roles so AI can detect role conflicts
      if (context.extractedData?.aiAnalyzedDetails?.targetRoles?.length) {
        const resumeRoles = context.extractedData.aiAnalyzedDetails.targetRoles;
        parts.push(`\nUSER'S RESUME ROLES: ${resumeRoles.join(', ')}`);
      }
      
      if (context.currentJobs?.length) {
        parts.push(`Currently showing ${context.currentJobs.length} job matches`);
        
        const sampleTitles = context.currentJobs
          .slice(0, 3)
          .map(job => `${job.title} at ${job.company}`)
          .join(', ');
        parts.push(`Top matches: ${sampleTitles}`);
      }
    }

    return parts.join('\n');
  }

  /**
   * Normalize text for consistent matching
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .replace(/['']/g, "'")  // Normalize apostrophes
      .replace(/[""]/g, '"')  // Normalize quotes
      .trim();
  }

  /**
   * Analyze chat history to determine what preferences have been collected
   * Uses context from assistant messages to understand what question was being answered
   */
  private analyzeCollectedPreferences(history: ChatMessage[]): string {
    const preferences: string[] = [];
    
    // Build pairs of assistant question -> user answer for context-aware matching
    const messagePairs: { question: string; answer: string }[] = [];
    for (let i = 0; i < history.length - 1; i++) {
      if (history[i].role === 'assistant' && history[i + 1].role === 'user') {
        messagePairs.push({
          question: this.normalizeText(history[i].content),
          answer: this.normalizeText(history[i + 1].content)
        });
      }
    }
    
    // Detect what question was being asked
    const isLocationQuestion = (q: string) => 
      /location|remote|on-?site|hybrid|where.*work|work.*where/i.test(q);
    const isSalaryQuestion = (q: string) => 
      /salary|compensation|pay|earning|money|range.*looking/i.test(q);
    const isCompanyQuestion = (q: string) => 
      /company|startup|enterprise|size|type.*company|company.*type|prefer.*work/i.test(q);
    
    // Check each Q&A pair for preference collection
    for (const { question, answer } of messagePairs) {
      // Location preferences - only count if responding to location question
      if (isLocationQuestion(question) && !preferences.some(p => p.includes('Location'))) {
        const locationPatterns = [
          { pattern: /\b(fully\s*remote|100%\s*remote|completely\s*remote)\b/, label: 'Fully remote' },
          { pattern: /\bremote\b/, label: 'Remote work preference' },
          { pattern: /\b(on-?site|in-?\s*office|in\s*person)\b/, label: 'On-site preference' },
          { pattern: /\bhybrid\b/, label: 'Hybrid preference' },
          { pattern: /\b(flexible|anywhere|any|open|either)\b/, label: 'Location flexible' }
        ];
        
        for (const { pattern, label } of locationPatterns) {
          if (pattern.test(answer)) {
            preferences.push(`- Location: ${label}`);
            break;
          }
        }
      }
      
      // Salary preferences - only count if responding to salary question
      if (isSalaryQuestion(question) && !preferences.some(p => p.includes('Salary'))) {
        const salaryPatterns = [
          { pattern: /\$[\d,]+k?|\d+k\b|\€[\d,]+k?/, label: 'Has specific expectations' },
          { pattern: /\b(no minimum|flexible|open|any|whatever|don'?t\s*mind|doesn'?t\s*matter)\b/i, label: 'Flexible/Open to any' }
        ];
        
        for (const { pattern, label } of salaryPatterns) {
          if (pattern.test(answer)) {
            preferences.push(`- Salary: ${label}`);
            break;
          }
        }
      }
      
      // Company preferences - only count if responding to company question
      if (isCompanyQuestion(question) && !preferences.some(p => p.includes('Company'))) {
        const companyPatterns = [
          { pattern: /\b(startup|start-?up|early\s*stage)\b/, label: 'Startup preference' },
          { pattern: /\b(enterprise|large\s*company|big\s*company|corporate)\b/, label: 'Enterprise/Large company' },
          { pattern: /\b(mid-?size|medium)\b/, label: 'Mid-size company' },
          { pattern: /\b(no\s*preference|any|either|don'?t\s*mind|flexible|open|whatever)\b/, label: 'Flexible on company type' }
        ];
        
        for (const { pattern, label } of companyPatterns) {
          if (pattern.test(answer)) {
            preferences.push(`- Company: ${label}`);
            break;
          }
        }
      }
    }
    
    // Check completion status
    const hasLocation = preferences.some(p => p.includes('Location'));
    const hasSalary = preferences.some(p => p.includes('Salary'));
    const hasCompany = preferences.some(p => p.includes('Company'));
    
    if (hasLocation && hasSalary && hasCompany) {
      preferences.push('\n*** ALL PREFERENCES COLLECTED - COMPLETE ONBOARDING ***');
    } else {
      const missing: string[] = [];
      if (!hasLocation) missing.push('location/remote');
      if (!hasSalary) missing.push('salary');
      if (!hasCompany) missing.push('company type');
      preferences.push(`\nStill need: ${missing.join(', ')}`);
    }
    
    return preferences.join('\n');
  }

  /**
   * Parse AI response and extract actions
   */
  private parseResponseWithActions(response: string): ChatResponse {
    let message = response;
    let actions: ChatAction[] = [];
    let suggestedQuickReplies: string[] = [];

    try {
      // Method 1: Match JSON blocks with {"actions": [...]} format
      const jsonRegex = /\{\s*"(?:actions|quickReplies)"\s*:\s*\[[\s\S]*?\](?:\s*,\s*"(?:actions|quickReplies)"\s*:\s*\[[\s\S]*?\])?\s*\}/g;
      const jsonMatches = response.match(jsonRegex);
      
      if (jsonMatches && jsonMatches.length > 0) {
        for (const jsonStr of jsonMatches) {
          try {
            const parsed = JSON.parse(jsonStr);
            
            if (parsed.actions?.length) {
              actions = [...actions, ...parsed.actions];
            }
            
            if (parsed.quickReplies?.length) {
              suggestedQuickReplies = parsed.quickReplies;
            }

            message = message.replace(jsonStr, '').trim();
          } catch {
          }
        }
      }
      
      // Method 2: Match individual action objects (e.g., numbered list: 1. {"type": ...})
      // This handles cases where the AI outputs actions as a list instead of wrapped in {"actions": [...]}
      if (actions.length === 0) {
        // Find all JSON objects that start with {"type" (action objects)
        const individualActions: ChatAction[] = [];
        
        // Look for patterns like: 1. {"type": ... or just {"type": ...
        // Use a more robust approach - find potential JSON starting points and parse them
        const potentialJsonStarts = [...response.matchAll(/(\d+\.\s*)?\{"type"\s*:/g)];
        
        for (const startMatch of potentialJsonStarts) {
          const startIndex = startMatch.index!;
          const jsonStartIndex = startMatch[0].indexOf('{') + startIndex;
          
          // Find the matching closing brace by counting braces
          let braceCount = 0;
          let endIndex = jsonStartIndex;
          
          for (let i = jsonStartIndex; i < response.length; i++) {
            if (response[i] === '{') braceCount++;
            if (response[i] === '}') braceCount--;
            if (braceCount === 0) {
              endIndex = i + 1;
              break;
            }
          }
          
          const jsonStr = response.substring(jsonStartIndex, endIndex);
          
          try {
            const actionObj = JSON.parse(jsonStr);
            if (actionObj.type && actionObj.payload) {
              individualActions.push(actionObj);
              // Remove from message (including the number prefix if present)
              const fullMatch = response.substring(startIndex, endIndex);
              message = message.replace(fullMatch, '').trim();
            }
          } catch {
          }
        }
        
        if (individualActions.length > 0) {
          actions = individualActions;
        }
      }
    } catch {
    }

    // Clean up message
    message = message
      .replace(/\s+/g, ' ')
      .replace(/^\s*[\r\n]+|[\r\n]+\s*$/g, '')
      .trim();

    // Generate contextual quick replies if none provided
    if (suggestedQuickReplies.length === 0) {
      suggestedQuickReplies = this.generateQuickReplies(message);
    }
    
    // Clear quick replies if we're performing an action (not asking a question)
    // This ensures quick replies only show when the AI is asking for user input
    if (actions.length > 0 && suggestedQuickReplies.length > 0) {
      const lowerMessage = message.toLowerCase();
      
      // Check if message contains a question (handle emojis at end)
      const hasQuestion = message.includes('?') || 
        lowerMessage.includes('what type') ||
        lowerMessage.includes('what kind') ||
        lowerMessage.includes('do you prefer') ||
        lowerMessage.includes('let me know');
      
      const isPerformingAction = 
        lowerMessage.includes('updating') || 
        lowerMessage.includes('refine') || 
        lowerMessage.includes('searching') ||
        lowerMessage.includes('just a moment') ||
        lowerMessage.includes("i'll find") ||
        lowerMessage.includes("i will find");
      
      // Only clear quick replies if NOT asking a question AND performing an action
      if (isPerformingAction && !hasQuestion) {
        suggestedQuickReplies = [];
      }
    }

    // SAFETY CHECK: Detect when AI mentions focusing on a role but forgot to add the action
    const rolePatterns = [
      /(?:focus(?:ed|ing)?|looking for|searching for|find(?:ing)?)\s+(?:on\s+)?(\w+(?:\s+\w+)?)\s+(?:roles?|jobs?|positions?)/i,
      /(?:i'll|i will|let me)\s+(?:focus on|look for|search for|find)\s+(\w+(?:\s+\w+)?)\s+(?:roles?|jobs?|positions?)/i,
      /you're (?:focused|interested) (?:on|in)\s+(\w+(?:\s+\w+)?)\s+(?:roles?|jobs?)?/i
    ];
    
    const roleAction = actions.find(a => a.type === 'add_signal' && a.payload?.category === 'role');
    const hasRoleAction = !!roleAction;
    
    // SAFETY CHECK 1: AI mentioned a role but forgot to add the action entirely
    if (!hasRoleAction) {
      for (const pattern of rolePatterns) {
        const match = message.match(pattern);
        if (match) {
          const mentionedRole = match[1].toLowerCase().trim();
          // Common design roles to recognize
          const designRoles = ['graphic design', 'graphic', 'ux design', 'ux', 'ui design', 'ui', 'product design', 'product', 'visual design', 'visual', 'web design', 'web'];
          
          if (designRoles.some(role => mentionedRole.includes(role))) {
            // Convert to proper role name
            let roleText = mentionedRole;
            if (mentionedRole === 'graphic' || mentionedRole === 'graphic design') roleText = 'graphic designer';
            else if (mentionedRole === 'ux' || mentionedRole === 'ux design') roleText = 'ux designer';
            else if (mentionedRole === 'ui' || mentionedRole === 'ui design') roleText = 'ui designer';
            else if (mentionedRole === 'product' || mentionedRole === 'product design') roleText = 'product designer';
            else if (mentionedRole === 'visual' || mentionedRole === 'visual design') roleText = 'visual designer';
            else if (mentionedRole === 'web' || mentionedRole === 'web design') roleText = 'web designer';
            
            actions.push({
              type: 'add_signal',
              payload: {
                category: 'role',
                tier: 'excellent_fit',
                text: roleText,
                source: 'chat',
                replaceAll: true // Remove resume roles when user explicitly chose this role
              }
            });
            break;
          }
        }
      }
    }
    
    // SAFETY CHECK 2: AI added role action but forgot replaceAll: true
    // When user explicitly chose a single role (AI says "I'll look for X roles for you"), 
    // we should replace all other roles including resume roles
    if (roleAction && !roleAction.payload?.replaceAll) {
      // Check if message suggests focusing on a SINGLE role (not "both")
      const singleRolePatterns = [
        /i'll (?:look|search|find) for (\w+(?:\s+\w+)?)\s+(?:roles?|jobs?)/i,
        /focus(?:ing|ed)? on (\w+(?:\s+\w+)?)\s+(?:roles?|jobs?)/i,
        /just (\w+(?:\s+\w+)?)\s+(?:roles?|jobs?)/i
      ];
      
      const suggestsSingleRole = singleRolePatterns.some(p => p.test(message));
      const mentionsBoth = /both|either|also|and\s+\w+\s+roles?/i.test(message);
      
      if (suggestsSingleRole && !mentionsBoth) {
        roleAction.payload.replaceAll = true;
      }
    }

    return {
      message: message || "How can I help you with your job search?",
      actions,
      suggestedQuickReplies
    };
  }

  /**
   * Generate contextual quick reply suggestions
   * Only generates quick replies when the AI is asking a question
   */
  private generateQuickReplies(message: string): string[] {
    const lowerMessage = message.toLowerCase();
    
    // Don't show quick replies if AI is confirming an action (not asking a question)
    if (lowerMessage.includes('updating') || 
        lowerMessage.includes('refine') || 
        lowerMessage.includes('searching') ||
        lowerMessage.includes('just a moment') ||
        lowerMessage.includes('let me find') ||
        lowerMessage.includes("i'll find") ||
        lowerMessage.includes("i will find")) {
      return [];
    }
    
    // Check if the message is asking for input (contains a question OR asks user to respond)
    const isAskingForInput = message.includes('?') || 
      lowerMessage.includes('let me know') ||
      lowerMessage.includes('what type') ||
      lowerMessage.includes('what kind') ||
      lowerMessage.includes('here are some options');
    
    if (!isAskingForInput) {
      return [];
    }
    
    if (lowerMessage.includes('location') || lowerMessage.includes('remote') || lowerMessage.includes('where')) {
      return ['Fully remote', 'Hybrid is fine', 'Any location'];
    }
    
    if (lowerMessage.includes('salary') || lowerMessage.includes('compensation') || lowerMessage.includes('pay')) {
      return ['$60k+', '$80k+', '$100k+', '$120k+', 'No minimum'];
    }
    
    if (lowerMessage.includes('company') || lowerMessage.includes('startup') || lowerMessage.includes('enterprise') || lowerMessage.includes('size')) {
      return ['Startups', 'Mid-size', 'Enterprise', 'No preference'];
    }
    
    if (lowerMessage.includes('role') || lowerMessage.includes('position') || lowerMessage.includes('level')) {
      return ['Senior roles', 'Lead positions', 'Open to any'];
    }

    return [];
  }

  /**
   * Clear chat history for a user (useful for testing or reset)
   */
  async clearChatHistory(userId: string, sessionType?: string): Promise<void> {
    try {
      let query = this.supabaseService
        .getClient()
        .from('chat_history')
        .delete()
        .eq('user_id', userId);

      if (sessionType) {
        query = query.eq('session_type', sessionType);
      }

      const { error } = await query;

      if (error) {
        throw error;
      }
    } catch (error) {
      throw error;
    }
  }
}